import { userService } from "@/app/api/user";
import type {
  CsvUploadTranslationResponse,
  CandidateResponse,
  UserCandidateResponse,
  AllScreenersResponse,
  ScreenerOrArchivedScreener,
  TableScreenings,
} from "@/types/screeners";
import type { UniversalSearchResult } from "@/types/universal-search";
import type { Nullable } from "@/types/utils";

export const getAllScreeners = async (query: string) => {
  try {
    const response = await userService.axiosWithUserAuth.get<
      AllScreenersResponse<ScreenerOrArchivedScreener>
    >(`/screener?${query}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching screeners:", error);
    throw error;
  }
};

export const restoreArchivedScreener = async (screenerId: string) => {
  try {
    const response = await userService.axiosWithUserAuth.post<void>(
      `/screener/cx/restore/${screenerId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error restoring screener:", error);
    throw error;
  }
};

// Get all screenings for a specific Job / Screener Location
export const getScreeningsByLocation = async (
  screenerLocationId: string,
  queryString?: string,
): Promise<TableScreenings> => {
  try {
    const url = `/screener-session/cx/screener-location/${screenerLocationId}${
      queryString ? `?${queryString}` : ""
    }`;
    const response =
      await userService.axiosWithUserAuth.get<TableScreenings>(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching screening data:", error);
    throw error;
  }
};

// Get all screenings for a specific Candidate
export const getCandidateScreenings = async (
  email: string,
  sessionId?: string,
): Promise<UserCandidateResponse> => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<UserCandidateResponse>(
        `/screener-session/cx/email/${email}${sessionId ? `/?screener-session-id=${sessionId}` : ""}`,
      );
    return {
      ...response.data,
      // The candidate endpoint returns competencyFields/showInSummaryFields,
      // but the location endpoint returns competencies/showInSummary.
      // Remap here so consumers get a consistent shape regardless of source.
      screenerSessions: response.data.screenerSessions.map(
        ({
          competencyFields,
          showInSummaryFields,
          showInSummaryMeta,
          ...session
        }) => ({
          ...session,
          competencies: competencyFields,
          showInSummary: showInSummaryFields,
          showInSummaryMeta,
        }),
      ),
    };
  } catch (error) {
    console.error("Error fetching candidate data:", error);
    throw error;
  }
};

export const highlightScreenerSession = async (
  id: string,
  value: Nullable<string>,
): Promise<CandidateResponse> => {
  try {
    const response =
      await userService.axiosWithUserAuth.patch<CandidateResponse>(
        `/screener-session/${id}`,
        {
          highlightColor: value,
        },
      );
    return response.data;
  } catch (error) {
    console.error("Error editing screening:", error);
    throw error;
  }
};

export const bulkHighlightScreenerSession = async (
  sessionIds: string[],
  highlightColor: Nullable<string>,
): Promise<CandidateResponse> => {
  try {
    const response =
      await userService.axiosWithUserAuth.patch<CandidateResponse>(
        `/screener-session/session-bulk-update`,
        {
          highlightColor,
          sessionIds,
        },
      );
    return response.data;
  } catch (error) {
    console.error("Error editing screening:", error);
    throw error;
  }
};

export const bulkSetSessionViewedStatus = async (
  sessionIds: string[],
  viewedAt: Nullable<string>,
) => {
  try {
    const response =
      await userService.axiosWithUserAuth.patch<CandidateResponse>(
        "/screener-session/session-bulk-update",
        { viewedAt, sessionIds },
      );
    return response.data;
  } catch (error) {
    console.error("Error editing screening:", error);
    throw error;
  }
};

export const getSessionCsv = async (
  screenerLocationId: string,
): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.get<string>(
      `/screener-session/cx/csv/${screenerLocationId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching screening csv:", error);
    throw error;
  }
};

export const getBulkSessionCsv = async (
  screenerLocationIds: string[],
): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.post<string>(
      "/screener-session/cx/csv/bulk",
      {
        locationIds: screenerLocationIds,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching bulk screening csv:", error);
    throw error;
  }
};

export const getBulkSessionCsvAll = async (): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.post<string>(
      "/screener-session/cx/csv/bulk-all",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching all screening csv:", error);
    throw error;
  }
};

export const getLocationSummaryCsv = async (
  screenerLocationIds: string[],
): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.post<string>(
      "/screener-session/cx/csv/location-summary",
      {
        locationIds: screenerLocationIds,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching location summary csv:", error);
    throw error;
  }
};

export const getLocationSummaryCsvAll = async (): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.post<string>(
      "/screener-session/cx/csv/location-summary-all",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching all location summary csv:", error);
    throw error;
  }
};

export const downloadPdf = async (pdfId: string) => {
  const response = await userService.axiosWithUserAuth.get(
    `/superscreener-pdf/url/${pdfId}`,
  );

  // initiate browser download
  const { signedDownloadUrl } = response.data;
  const link = Object.assign(document.createElement("a"), {
    href: signedDownloadUrl,
    target: "_blank",
    download: "screener.pdf",
  });
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return response.data;
};

export const getPdfViewUrl = async (pdfId: string): Promise<string> => {
  const response = await userService.axiosWithUserAuth.get(
    `/superscreener-pdf/url/${pdfId}`,
  );
  return response.data.signedUrl;
};

export const viewPdf = async (pdfId: string) => {
  const signedUrl = await getPdfViewUrl(pdfId);
  window.open(signedUrl, "_blank");
};

export const reprocessScreenerSession = async (ids: string) => {
  const response = await userService.axiosWithUserAuth.get(
    `/screener-session/reprocess/${ids}`,
  );
  return response.data;
};

export const markSessionViewed = async (sessionId: string) => {
  const response = await userService.axiosWithUserAuth.get(
    `/screener-session/viewed/${sessionId}`,
  );

  return response.data;
};

export const uploadCsv = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await userService.axiosWithUserAuth.post<string>(
      `/screener/csv/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading csv:", error);
    throw error;
  }
};

export const uploadTranslationCsv = async (
  file: File,
): Promise<CsvUploadTranslationResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response =
      await userService.axiosWithUserAuth.post<CsvUploadTranslationResponse>(
        `/translation/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    return response.data;
  } catch (error) {
    console.error("Error uploading translation csv:", error);
    throw error;
  }
};

export const createBulkShortUrls = async (
  s3Keys: string[],
): Promise<Record<string, string>> => {
  const response = await userService.axiosWithUserAuth.post<{
    urls: Record<string, string>;
  }>("/short-url/bulk", { s3Keys });
  return response.data.urls;
};

export const createShortUrl = async (
  s3Key: string,
  candidateName?: string,
): Promise<string> => {
  const response = await userService.axiosWithUserAuth.post<{ url: string }>(
    "/short-url",
    { s3Key, candidateName },
  );
  return response.data.url;
};

export const getUniversalSearchResults = async (query: string) => {
  try {
    const response = await userService.axiosWithUserAuth.get<
      UniversalSearchResult[]
    >(`screener/search?query=${encodeURIComponent(query)}`);

    return response.data;
  } catch (error) {
    console.error("Error fetching Universal Search results:", error);
    throw error;
  }
};
